import { Fragment } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { tabBar, type Palette } from "@heraion/shared";
import { Icon, type IconName } from "@heraion/ui";
import { CreateNavButton } from "./CreateNavButton";
import { stateAria } from "@heraion/ui";

export type TabBarItem = { key: string; label: string; icon: IconName };

// The bar's own content height, EXCLUDING the bottom safe-area inset (which
// AppShell adds on top, both here as `paddingBottom` and to the scrollable
// body's own bottom padding, so content never sits underneath the overlay).
export const TAB_BAR_HEIGHT = 56;

/** How far the create button sticks out ABOVE the bar's top edge.
 *  ⛔ Not decoration, and not a free number: the circle is 48 tall inside a
 *  56 bar, so centring it leaves 4, and `createSlot`'s `marginTop: -16`
 *  lands it 12 above the edge. The host MUST add this to the scrollable
 *  body's bottom padding on top of TAB_BAR_HEIGHT, or the last 12px of real
 *  content sit behind the button — which is also above it in the stacking
 *  order, so it steals the tap too. Found by jl-ux-a11y-auditor, 20/09,
 *  after a first pass padded for the bar alone. */
export const CREATE_NAV_OVERHANG = 12;

// RW-52 — the narrow-viewport (< WIDE_BREAKPOINT) nav bar. On mobile the tab
// bar is fixed to the SCREEN BOTTOM (react-navigation's bottom tabs); that is
// the point of this fetta — a narrow browser must read like the phone, not a
// strip under the TopBar (where this lived before, see AppShell's prior
// `navBar`/`navInner`). Overlays the scrollable body (`position: "absolute"`,
// pinned bottom — a portable RN style, not web-only CSS); AppShell pads the
// body by TAB_BAR_HEIGHT + CREATE_NAV_OVERHANG + the bottom inset so
// nothing sits underneath it — see CREATE_NAV_OVERHANG above.
// Extracted out of AppShell.tsx to keep it under the size cap, mirroring the
// existing Sidebar/TopBar split.
export function TabBar({
  p,
  items,
  activeKey,
  onSelect,
  createAfterKey,
  onCreatePress,
  createLabel,
  bottomInset,
}: {
  p: Palette;
  items: TabBarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** RW-52 — mounts the Create affordance right after this item's key
   *  (`workouts` for athlete, `coachDashboard` for coach — mobile's own FAB
   *  position). Undefined → no affordance (admin has none, matching mobile). */
  createAfterKey?: string;
  onCreatePress?: () => void;
  createLabel?: string;
  /** The device's bottom safe-area inset (home indicator / gesture bar). */
  bottomInset: number;
}) {
  const styles = makeStyles(p, bottomInset);
  return (
    <View style={styles.bar}>
      <View style={styles.inner} accessibilityRole="tablist">
        {items.map((it) => {
          const on = it.key === activeKey;
          return (
            <Fragment key={it.key}>
              <View style={styles.tabSlot}>
                <Pressable
                  style={[styles.tab, on && styles.tabOn]}
                  onPress={() => onSelect(it.key)}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: on }}
                  {...stateAria({ selected: on }, "tab")}
                  accessibilityLabel={it.label}
                >
                  <Icon
                    name={it.icon}
                    size={tabBar.iconSize}
                    color={on ? p.ink : p.ink3}
                  />
                  <Text
                    numberOfLines={1}
                    style={[styles.tabLabel, on && styles.tabLabelOn]}
                  >
                    {it.label}
                  </Text>
                </Pressable>
              </View>
              {it.key === createAfterKey && onCreatePress && createLabel ? (
                <View style={styles.createSlot}>
                  <CreateNavButton
                    p={p}
                    variant="fab"
                    onPress={onCreatePress}
                    accessibilityLabel={createLabel}
                  />
                </View>
              ) : null}
            </Fragment>
          );
        })}
      </View>
    </View>
  );
}

function makeStyles(p: Palette, bottomInset: number) {
  return StyleSheet.create({
    bar: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: p.surface,
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingBottom: bottomInset,
    },
    inner: {
      flexDirection: "row",
      alignItems: "center",
      height: TAB_BAR_HEIGHT,
      paddingHorizontal: 8,
    },
    tabSlot: { flex: 1, minWidth: 0, alignItems: "stretch" },
    tab: {
      minHeight: tabBar.itemMinHeight,
      minWidth: 0,
      paddingHorizontal: 2,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: 10,
    },
    tabOn: { backgroundColor: p.surface2 },
    tabLabel: {
      color: p.ink3,
      fontSize: tabBar.labelSize,
      fontWeight: tabBar.labelWeight,
      maxWidth: "100%",
    },
    tabLabelOn: { color: p.ink, fontWeight: tabBar.labelWeightActive },
    // No flex, no label — a fixed-size slot raised above the bar's own top
    // edge (mirrors CreateFab's `top: -22` over the native tab bar), so it
    // protrudes exactly like mobile's FAB instead of sitting flush in the row.
    createSlot: {
      alignItems: "center",
      // -16 on a 48 circle in a 56 bar ⇒ CREATE_NAV_OVERHANG (12) above
      // the edge. Change one and the other must follow.
      marginTop: -16,
      paddingHorizontal: 4,
    },
  });
}
